import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

import {
    trackUserRecommendationsViewLessClick,
    trackUserRecommendationsViewMoreClick,
} from './userRecommendationsTracking.ts';

type UseUserRecommendationsGridExpandArgs = {
    trackingTrigger: string;
    autoLoadMore: boolean;
    hasMoreToLoad: boolean;
    onLoadMore: () => void;
};

type UseUserRecommendationsGridExpandResult = {
    isExpanded: boolean;
    moduleRef: RefObject<HTMLDivElement | null>;
    showViewLess: boolean;
    showToggleButton: boolean;
    handleLoadMore: () => void;
    handleToggleExpand: () => void;
};

export const useUserRecommendationsGridExpand = ({
    trackingTrigger,
    autoLoadMore,
    hasMoreToLoad,
    onLoadMore,
}: UseUserRecommendationsGridExpandArgs): UseUserRecommendationsGridExpandResult => {
    const [isExpanded, setIsExpanded] = useState(false);
    const moduleRef = useRef<HTMLDivElement>(null);
    const shouldScrollToModuleTopRef = useRef(false);

    const showViewMore = !autoLoadMore && !isExpanded && hasMoreToLoad;
    const showViewLess = !autoLoadMore && isExpanded;
    const showToggleButton = showViewMore || showViewLess;

    const handleLoadMore = useCallback(() => {
        if (isExpanded) {
            return;
        }
        onLoadMore();
        setIsExpanded(true);
    }, [isExpanded, onLoadMore]);

    const handleToggleExpand = useCallback(() => {
        if (isExpanded) {
            shouldScrollToModuleTopRef.current = true;
            trackUserRecommendationsViewLessClick(trackingTrigger);
            setIsExpanded(false);
            return;
        }
        trackUserRecommendationsViewMoreClick(trackingTrigger);
        handleLoadMore();
    }, [handleLoadMore, isExpanded, trackingTrigger]);

    useEffect(() => {
        if (!shouldScrollToModuleTopRef.current || !moduleRef.current) {
            return;
        }
        shouldScrollToModuleTopRef.current = false;
        moduleRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [isExpanded]);

    return {
        isExpanded,
        moduleRef,
        showViewLess,
        showToggleButton,
        handleLoadMore,
        handleToggleExpand,
    };
};
